// Iter 153.23 (Feb 8, 2026, Yash) — AI Auto-Placed Orders ledger.
// Super-admin-only page surfacing every order the AI auto-placed via
// `_try_auto_place_admin_order` (server.py) so the team can corroborate
// against the upstream PHP API and the ops inbox at month-end.
//
// Source endpoint: GET /api/admin/ai-placed-orders
//   - Filters: date_from, date_to (inclusive), amount_min, amount_max
//   - Pagination: page, page_size (server caps page_size at 500)
//   - Returns { orders, total, page, page_size, stats }
// CSV export: GET /api/admin/ai-placed-orders/export (max 5,000 rows).
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../lib/api";
import { Bot, Download, ExternalLink, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};
// Default to "this month" so super admin lands on a useful view.
const _firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const _todayISO = () => new Date().toISOString().slice(0, 10);

export default function AiOrdersPage() {
  const [filters, setFilters] = useState({
    date_from: _firstOfMonthISO(),
    date_to: _todayISO(),
    amount_min: "",
    amount_max: "",
  });
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ count: 0, total_amount: 0, total_codcharges: 0, total_discount: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(filters.amount_min !== "" ? { amount_min: filters.amount_min } : {}),
        ...(filters.amount_max !== "" ? { amount_max: filters.amount_max } : {}),
      };
      const { data } = await adminApi.listAiPlacedOrders(params);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setStats(data.stats || { count: 0, total_amount: 0, total_codcharges: 0, total_discount: 0 });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Super admin access required");
      } else {
        toast.error("Failed to load AI orders");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(filters.amount_min !== "" ? { amount_min: filters.amount_min } : {}),
        ...(filters.amount_max !== "" ? { amount_max: filters.amount_max } : {}),
      };
      const { data } = await adminApi.exportAiPlacedOrdersCsv(params);
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-placed-orders-${filters.date_from || "all"}_${filters.date_to || "now"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="ai-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Bot className="w-6 h-6 text-blue-600" />
            AI Auto-Placed Orders
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Every admin order placed automatically by the AI on the website. Use for monthly recon.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="ai-orders-refresh-btn"
            className="px-3 py-2 border rounded-md hover:bg-gray-50 inline-flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="ai-orders-export-csv-btn"
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="ai-orders-stats-cards">
        <StatCard label="Orders placed" value={stats.count} testid="stat-count" />
        <StatCard label="Total value" value={fmtINR(stats.total_amount)} testid="stat-total" />
        <StatCard label="COD charges sent" value={fmtINR(stats.total_codcharges)} testid="stat-cod" />
        <StatCard label="Flat discounts given" value={fmtINR(stats.total_discount)} testid="stat-discount" />
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-4" data-testid="ai-orders-filters">
        <div className="flex items-center gap-2 text-sm font-medium mb-3 text-gray-700">
          <Filter className="w-4 h-4" />
          Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input
            label="From date"
            type="date"
            value={filters.date_from}
            onChange={(v) => { setPage(1); setFilters({ ...filters, date_from: v }); }}
            testid="filter-date-from"
          />
          <Input
            label="To date"
            type="date"
            value={filters.date_to}
            onChange={(v) => { setPage(1); setFilters({ ...filters, date_to: v }); }}
            testid="filter-date-to"
          />
          <Input
            label="Min amount (₹)"
            type="number"
            placeholder="0"
            value={filters.amount_min}
            onChange={(v) => { setPage(1); setFilters({ ...filters, amount_min: v }); }}
            testid="filter-amount-min"
          />
          <Input
            label="Max amount (₹)"
            type="number"
            placeholder="any"
            value={filters.amount_max}
            onChange={(v) => { setPage(1); setFilters({ ...filters, amount_max: v }); }}
            testid="filter-amount-max"
          />
          <button
            onClick={() => {
              setPage(1);
              setFilters({ date_from: "", date_to: "", amount_min: "", amount_max: "" });
            }}
            data-testid="filter-clear-btn"
            className="self-end px-3 py-2 border rounded-md hover:bg-gray-50 text-sm"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-orders-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <Th>Order #</Th>
                <Th>Placed at</Th>
                <Th>Customer</Th>
                <Th>Phone</Th>
                <Th>Payment</Th>
                <Th align="right">Amount</Th>
                <Th align="right">COD</Th>
                <Th align="right">Discount</Th>
                <Th>Items</Th>
                <Th>Status</Th>
                <Th>Chat</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="p-8 text-center text-gray-500">Loading…</td></tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-gray-500" data-testid="ai-orders-empty">
                    No AI auto-placed orders match these filters.
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="border-b hover:bg-gray-50" data-testid={`ai-order-row-${o.order_number}`}>
                    <Td className="font-mono">
                      <span data-testid={`order-num-${o.order_number}`}>#{o.order_number}</span>
                    </Td>
                    <Td className="whitespace-nowrap">{fmtDateTime(o.created_at)}</Td>
                    <Td>{o.customer_name || "—"}</Td>
                    <Td className="font-mono">{o.customer_phone || "—"}</Td>
                    <Td>
                      <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">
                        {o.payment_mode || "—"}
                      </span>
                    </Td>
                    <Td align="right" className="font-medium">{fmtINR(o.amount)}</Td>
                    <Td align="right" className="text-orange-700">
                      {o.codcharges ? fmtINR(o.codcharges) : "—"}
                    </Td>
                    <Td align="right" className="text-green-700">
                      {o.custom_reduce ? fmtINR(o.custom_reduce) : "—"}
                    </Td>
                    <Td className="max-w-xs truncate" title={o.items_summary}>
                      {o.items_summary || "—"}
                    </Td>
                    <Td>
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">
                        {o.status || "placed_order"}
                      </span>
                    </Td>
                    <Td>
                      <Link
                        to={`/leads/${o.lead_id}`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"
                        data-testid={`view-lead-chat-${o.order_number}`}
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </Link>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between p-3 border-t bg-gray-50" data-testid="ai-orders-pagination">
            <div className="text-sm text-gray-600">
              Showing <strong>{(page - 1) * pageSize + 1}</strong>–
              <strong>{Math.min(page * pageSize, total)}</strong> of <strong>{total}</strong>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="page-prev-btn"
                className="px-3 py-1 border rounded text-sm hover:bg-white disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-3 py-1 text-sm">
                Page <strong>{page}</strong> / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="page-next-btn"
                className="px-3 py-1 border rounded text-sm hover:bg-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border rounded-lg p-4" data-testid={testid}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>
);

const Input = ({ label, value, onChange, type = "text", placeholder = "", testid }) => (
  <div>
    <label className="block text-xs text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-2 text-${align} font-medium text-gray-700 text-xs uppercase tracking-wide`}>
    {children}
  </th>
);

const Td = ({ children, className = "", align = "left" }) => (
  <td className={`px-4 py-2 text-${align} ${className}`}>{children}</td>
);
